// Static UI strings for the website.
// 14 supported languages. RU/EN are fully translated everywhere; other languages
// fall back to EN inside `pickLang` when a specific translation is missing.

export type Lang =
  | "ru" | "ky" | "en" | "uk" | "be" | "fr" | "zh"
  | "tr" | "ko" | "de" | "hi" | "ar" | "ja" | "es";

export const ALL_LANGS: Lang[] = [
  "ru","ky","en","uk","be","fr","zh","tr","ko","de","hi","ar","ja","es",
];

export const RTL_LANGS: Lang[] = ["ar"];

export const LANG_META: Record<Lang, { code: string; htmlLang: string; ogLocale: string; nativeName: string; dir: "ltr" | "rtl" }> = {
  ru: { code: "ru", htmlLang: "ru", ogLocale: "ru_RU", nativeName: "Русский", dir: "ltr" },
  ky: { code: "ky", htmlLang: "ky", ogLocale: "ky_KG", nativeName: "Кыргызча", dir: "ltr" },
  en: { code: "en", htmlLang: "en", ogLocale: "en_US", nativeName: "English", dir: "ltr" },
  uk: { code: "uk", htmlLang: "uk", ogLocale: "uk_UA", nativeName: "Українська", dir: "ltr" },
  be: { code: "be", htmlLang: "be", ogLocale: "be_BY", nativeName: "Беларуская", dir: "ltr" },
  fr: { code: "fr", htmlLang: "fr", ogLocale: "fr_FR", nativeName: "Français", dir: "ltr" },
  zh: { code: "zh", htmlLang: "zh", ogLocale: "zh_CN", nativeName: "中文", dir: "ltr" },
  tr: { code: "tr", htmlLang: "tr", ogLocale: "tr_TR", nativeName: "Türkçe", dir: "ltr" },
  ko: { code: "ko", htmlLang: "ko", ogLocale: "ko_KR", nativeName: "한국어", dir: "ltr" },
  de: { code: "de", htmlLang: "de", ogLocale: "de_DE", nativeName: "Deutsch", dir: "ltr" },
  hi: { code: "hi", htmlLang: "hi", ogLocale: "hi_IN", nativeName: "हिन्दी", dir: "ltr" },
  ar: { code: "ar", htmlLang: "ar", ogLocale: "ar_AE", nativeName: "العربية", dir: "rtl" },
  ja: { code: "ja", htmlLang: "ja", ogLocale: "ja_JP", nativeName: "日本語", dir: "ltr" },
  es: { code: "es", htmlLang: "es", ogLocale: "es_ES", nativeName: "Español", dir: "ltr" },
};

export const STRINGS = {
  nav: {
    home: { ru: "Главная", en: "Home" },
    about: { ru: "О нас", en: "About" },
    catalog: { ru: "Каталог", en: "Catalog" },
    contacts: { ru: "Контакты", en: "Contacts" },
  },
  hero: {
    bookCta: { ru: "Забронировать авто", en: "Book a car" },
  },
  catalog: {
    moreBtn: { ru: "Подробнее", en: "Details" },
    pricePerDayPrefix: { ru: "от", en: "from" },
    pricePerDaySuffix: { ru: "/день", en: "/day" },
    emptyCategory: {
      ru: "Нет автомобилей в этой категории. Добавьте их в /admin.",
      en: "No cars in this category. Add some in /admin.",
    },
    backBtn: { ru: "Назад", en: "Back" },
    bookBtn: { ru: "Забронировать", en: "Book" },
    days_1_5: { ru: "1-5 дней", en: "1-5 days" },
    days_5_10: { ru: "5-10 дней", en: "5-10 days" },
    days_10_plus: { ru: "10+ дней", en: "10+ days" },
    photoLabel: { ru: "Фото", en: "Photo" },
  },
  scrollTop: { ru: "Наверх", en: "Top" },
  consult: {
    formSubmittedTitle: { ru: "Заявка отправлена", en: "Request sent" },
    formSubmittedDesc: {
      ru: "Мы свяжемся с вами в ближайшее время.",
      en: "We'll get back to you shortly.",
    },
  },
  footer: {
    phoneLabel: { ru: "Телефон", en: "Phone" },
    addressLabel: { ru: "Адрес", en: "Address" },
    openIn2gis: { ru: "Открыть в 2ГИС →", en: "Open in 2GIS →" },
    mapTitle: {
      ru: "Карта: Бишкек, ул. Султана Ибраимова, 115/4",
      en: "Map: Bishkek, Sultan Ibraimov street, 115/4",
    },
  },
  chat: {
    launcher: { ru: "ИИ-консультант", en: "AI Consultant" },
    launcherAria: {
      ru: "Открыть ИИ-консультанта",
      en: "Open AI Consultant",
    },
    teaserTitle: {
      ru: "Нужна помощь с выбором?",
      en: "Need help choosing?",
    },
    teaserText: {
      ru: "Спросите ИИ-консультанта — подберу авто под вашу поездку 👇",
      en: "Ask the AI consultant — I'll pick a car for your trip 👇",
    },
    teaserClose: { ru: "Закрыть подсказку", en: "Dismiss" },
    headerTitle: { ru: "ИИ-консультант", en: "AI Consultant" },
    headerSubtitle: {
      ru: "Подберу авто под вашу поездку",
      en: "I'll help you pick a car for your trip",
    },
    reset: { ru: "Сброс", en: "Reset" },
    close: { ru: "Закрыть", en: "Close" },
    inputPlaceholder: { ru: "Напишите сообщение...", en: "Type a message..." },
    sendAria: { ru: "Отправить", en: "Send" },
    greeting: {
      ru: "Привет! Я ИИ-консультант Freeways. Помогу подобрать авто под вашу поездку. Расскажите, пожалуйста, для чего вам нужна машина? (например: поездка в горы, по городу, в аэропорт, командировка)",
      en: "Hi! I'm the Freeways AI consultant. I'll help you pick a car for your trip. Tell me — what do you need the car for? (e.g. trip to the mountains, city, airport, business trip)",
    },
    recommendationLabel: { ru: "Рекомендация", en: "Recommendation" },
    bookWhatsApp: {
      ru: "Забронировать в WhatsApp",
      en: "Book on WhatsApp",
    },
    tooManyRequestsTitle: { ru: "Слишком много запросов", en: "Too many requests" },
    tooManyRequestsDesc: { ru: "Попробуйте через минуту.", en: "Please try again in a minute." },
    unavailableTitle: { ru: "Недоступно", en: "Unavailable" },
    unavailableDesc: {
      ru: "ИИ-консультант временно недоступен.",
      en: "AI Consultant is temporarily unavailable.",
    },
    errorTitle: { ru: "Ошибка", en: "Error" },
    errorDesc: { ru: "Не удалось получить ответ.", en: "Failed to get a response." },
    networkErrorDesc: {
      ru: "Проверьте подключение к интернету.",
      en: "Check your internet connection.",
    },
    fallbackRecommended: {
      ru: (name: string, reason: string) =>
        `Отличный вариант для вас — **${name}**. ${reason}`,
      en: (name: string, reason: string) =>
        `A great pick for you — **${name}**. ${reason}`,
    },
    fallbackPhoto: {
      ru: (name: string) => `Вот фото: **${name}**.`,
      en: (name: string) => `Here's a photo: **${name}**.`,
    },
  },
  langSwitcher: {
    ariaLabel: { ru: "Сменить язык", en: "Change language" },
  },
} as const;

export type StringPath = string;

// Accepts either {ru, en} pairs or partial maps with more languages.
// Falls back: requested -> en -> ru.
export function pickLang<T>(obj: Partial<Record<Lang, T>> & { ru: T; en: T }, lang: Lang): T {
  const v = obj[lang];
  if (v !== undefined) return v;
  return (obj.en ?? obj.ru) as T;
}

// Pick localized text from a pair of fields (ru, en).
// For non-RU languages prefer EN; if EN is empty, fall back to RU.
export function pickText(ru: string | null | undefined, en: string | null | undefined, lang: Lang): string {
  if (lang !== "ru") {
    const e = (en ?? "").trim();
    if (e) return e;
  }
  return ru ?? "";
}

// Same as pickText but, when EN is empty, tries to look up the RU text in a
// provided RU→EN dictionary before falling back to RU.
export function pickTextWithDict(
  ru: string | null | undefined,
  en: string | null | undefined,
  lang: Lang,
  dict: Record<string, string>,
): string {
  if (lang !== "ru") {
    const e = (en ?? "").trim();
    if (e) return e;
    const key = (ru ?? "").trim();
    if (key && dict[key]) return dict[key];
  }
  return ru ?? "";
}

// Lightweight auto-translation for common Russian patterns in user-entered
// short labels (e.g. "от $250/день" → "from $250/day", "до 1 500 сом / сутки"
// → "to 1 500 KGS / day"). Only used when EN field is empty so admins can
// always override.
//
// Notes on robustness:
//  - JS `\b` does not recognize Cyrillic word boundaries, so we use explicit
//    Unicode lookarounds: (?<![\p{L}\p{N}]) … (?![\p{L}\p{N}]).
//  - Order matters: longer/multi-word phrases are matched BEFORE shorter
//    single-word fallbacks so e.g. "в сутки" → "/day" wins over "сутки".
//  - Numbers (including "1 500", "1.5", "1,500") are never modified — we
//    only touch alphabetic Russian tokens and a few currency abbreviations.
//  - Slash separators are normalized: "/день", "/ день", " в день" all map
//    to "/day".

// Helper: match a Russian word with non-letter/digit boundaries on both sides.
// Group around alternatives is required because the regex contains '|'.
const w = (alt: string, flags = "giu") =>
  new RegExp(`(?<![\\p{L}\\p{N}])(?:${alt})(?![\\p{L}\\p{N}])`, flags);

type Rule = [RegExp, string | ((m: string) => string)];

// Apply in order. Each rule is independent and case-insensitive.
const RU_EN_RULES: Rule[] = [
  // ---- Time units after a slash or "в" (per-X pricing) -----------------
  // "/день", " / сутки", " в день", " в сутки", "/ час" → "/day" or "/h"
  [/\s*[\/\u2044]\s*(?:сутки|сут\.?|день|дня|дней)(?![\p{L}\p{N}])/giu, "/day"],
  [/\s*[\/\u2044]\s*(?:час(?:ов|а)?|ч\.?)(?![\p{L}\p{N}])/giu, "/h"],
  [/\s*[\/\u2044]\s*(?:минут[аы]?|мин\.?)(?![\p{L}\p{N}])/giu, "/min"],
  [/\s*[\/\u2044]\s*(?:недел[яиюей]|нед\.?)(?![\p{L}\p{N}])/giu, "/week"],
  [/\s*[\/\u2044]\s*(?:месяц(?:ев|а)?|мес\.?)(?![\p{L}\p{N}])/giu, "/month"],

  [/(?<![\p{L}\p{N}])в\s+(?:сутки|день)(?![\p{L}\p{N}])/giu, "/day"],
  [/(?<![\p{L}\p{N}])в\s+час(?![\p{L}\p{N}])/giu, "/h"],
  [/(?<![\p{L}\p{N}])в\s+(?:неделю|нед\.?)(?![\p{L}\p{N}])/giu, "/week"],
  [/(?<![\p{L}\p{N}])в\s+месяц(?![\p{L}\p{N}])/giu, "/month"],

  // "за день/сутки/час..."
  [/(?<![\p{L}\p{N}])за\s+(?:сутки|день)(?![\p{L}\p{N}])/giu, "/day"],
  [/(?<![\p{L}\p{N}])за\s+час(?![\p{L}\p{N}])/giu, "/h"],

  // ---- Range / comparison words ----------------------------------------
  [w("от"), "from"],
  [w("до"), "to"],
  [w("свыше|более"), "over"],

  // ---- Currencies ------------------------------------------------------
  // Russian rouble: руб., рублей, рубля, рубль, ₽
  [w("руб(?:лей|ля|ль)?\\.?"), "RUB"],
  [/₽/g, "RUB"],
  // Kyrgyz som
  [w("сом(?:ов|а|ы)?"), "KGS"],
  // USD / EUR shorthands sometimes typed in Russian
  [w("долл(?:аров|ара|ар)?\\.?|дол\\.?|usd"), "USD"],
  [w("евро|eur"), "EUR"],

  // ---- Standalone time-unit fallbacks (when not after a separator) -----
  // These run last so multi-word forms above already consumed their inputs.
  [w("сут(?:ки|ок)?\\.?"), "day"],
  [w("дн(?:ей|я|ём|ем)|день"), "day"],
  [w("час(?:ов|а)?|ч\\.?"), "h"],
  [w("минут[аы]?|мин\\.?"), "min"],
  [w("недел[яиюей]|нед\\.?"), "week"],
  [w("месяц(?:ев|а)?|мес\\.?"), "month"],
];

export function autoTranslateShort(text: string): string {
  if (!text) return text;
  let out = text;
  for (const [re, repl] of RU_EN_RULES) {
    out = typeof repl === "string" ? out.replace(re, repl) : out.replace(re, repl);
  }
  // Collapse double spaces introduced by replacements, but keep "/day", "/h" tight.
  out = out.replace(/[ \t]{2,}/g, " ").replace(/\s+([\/.,;:])/g, "$1").trim();
  return out;
}

export function pickShortLabel(
  ru: string | null | undefined,
  en: string | null | undefined,
  lang: Lang,
): string {
  if (lang !== "ru") {
    const e = (en ?? "").trim();
    if (e) return e;
    const r = (ru ?? "").trim();
    if (r) return autoTranslateShort(r);
  }
  return ru ?? "";
}
